// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Bill review content renderer (detailed review analysis).
 * Used by BillRendererDialog to display overview + impact reports.
 */
import type { FC } from 'react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'

import type { BillReview } from '../../models/types'

function getScoreBadgeVariant(scoreType: string): 'default' | 'destructive' | 'secondary' {
  switch (scoreType) {
    case 'positive':
      return 'default'
    case 'negative':
      return 'destructive'
    default:
      return 'secondary'
  }
}

function ScoreIcon({ scoreType }: { scoreType: string }) {
  switch (scoreType) {
    case 'positive':
      return <TrendingUp className="h-4 w-4" />
    case 'negative':
      return <TrendingDown className="h-4 w-4" />
    default:
      return <Minus className="h-4 w-4" />
  }
}

/** Content renderer for bill-review component type */
export const BillReviewContent: FC<{ data: unknown }> = ({ data }) => {
  const review = data as BillReview

  if (!review) return null

  const overview = review.reportOverview
  const reports = review.impactReports

  if (!overview && !reports) return null

  return (
    <div className="space-y-6 py-4">
      {/* Overall Rationale */}
      {overview?.rationale && (
        <Card>
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {overview.rationale}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Impact Reports */}
      {reports && reports.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Impact Reports</h3>
          {reports.map((report, reportIndex) => (
            <Card key={reportIndex}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{report.areaName}</CardTitle>
                  {report.relevanceScore && (
                    <Badge
                      variant={getScoreBadgeVariant(report.relevanceScore.scoreType)}
                      className="gap-1 flex-shrink-0 ml-2"
                    >
                      <ScoreIcon scoreType={report.relevanceScore.scoreType} />
                      {report.relevanceScore.score}/10
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {report.rationale}
                </p>

                {report.citations && report.citations.length > 0 && (
                  <div className="space-y-2 pt-2 border-t">
                    <p className="text-xs font-medium text-muted-foreground">Supporting Citations</p>
                    {report.citations.map((citation, citationIndex) => (
                      <blockquote key={citationIndex} className="text-xs border-l-2 pl-3 italic text-muted-foreground whitespace-pre-wrap">
                        {citation}
                      </blockquote>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
